import { callGeotabAPI } from '../api/geotab-client';
import type { Rule, User, Device, Group } from '../api/types';
import { buildGroupHierarchy } from '../api/group-hierarchy';
import type { RuleKey, RuleConfig, SafetyPageData, SafetyDevice } from './safety-types';

// ── Rule-selection persistence ─────────────────────────────────────────────

const LS_KEY_RULE_IDS = 'safety_rule_ids';

export function saveRuleSelection(key: RuleKey, id: string): void {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY_RULE_IDS) ?? '{}');
    saved[key] = id;
    localStorage.setItem(LS_KEY_RULE_IDS, JSON.stringify(saved));
  } catch { /* quota */ }
}

function loadSavedRuleSelections(): Partial<Record<RuleKey, string>> {
  try { return JSON.parse(localStorage.getItem(LS_KEY_RULE_IDS) ?? '{}'); }
  catch { return {}; }
}

// ── Fuzzy-match keywords for each target rule ──────────────────────────────

const FUZZY: Record<RuleKey, string[]> = {
  hardAccel:      ['hard accel', 'harsh accel'],
  harshBraking:   ['harsh brak', 'hard brak'],
  harshCornering: ['harsh corner', 'hard corner'],
  seatBelt:       ['seat belt', 'seatbelt'],
  speeding:       ['speed'],
  idling:         ['idl'],
};

// Display labels for rule keys (used when rule is found but name looks odd)
export const RULE_LABELS: Record<RuleKey, string> = {
  hardAccel:      'Hard Acceleration',
  harshBraking:   'Harsh Braking',
  harshCornering: 'Harsh Cornering',
  seatBelt:       'Seat Belt',
  speeding:       'Speeding',
  idling:         'Idling',
};

// Default weights — all 6 enabled; sum = 100
const DEFAULT_WEIGHTS: Record<RuleKey, number> = {
  hardAccel: 35, harshBraking: 5, harshCornering: 5,
  seatBelt:  25, speeding:     25, idling:         5,
};

export const RULE_KEY_ORDER: RuleKey[] = [
  'hardAccel', 'harshBraking', 'harshCornering', 'seatBelt', 'speeding', 'idling',
];

const LS_KEY = 'safety_weights';

// ── Public loader ──────────────────────────────────────────────────────────

export async function loadSafetyPageData(): Promise<SafetyPageData> {
  const [rawRules, rawUsers, rawDevices, rawGroups] = await Promise.all([
    callGeotabAPI<Rule[]>  ('Get', { typeName: 'Rule',   resultsLimit: 10000 }),
    callGeotabAPI<User[]>  ('Get', { typeName: 'User',   resultsLimit: 5000  }),
    callGeotabAPI<Device[]>('Get', { typeName: 'Device', resultsLimit: 5000  }),
    callGeotabAPI<Group[]> ('Get', { typeName: 'Group',  resultsLimit: 1000  }),
  ]);

  const now = new Date();

  // ── Fuzzy-match the 6 target rules ────────────────────────────────────────
  const savedWeights     = loadSavedWeights();
  const savedSelections  = loadSavedRuleSelections();
  const ruleConfigs: RuleConfig[] = [];

  for (const key of RULE_KEY_ORDER) {
    const keywords = FUZZY[key];
    const matches = rawRules.filter(r =>
      r.id && r.name && keywords.some(kw => r.name.toLowerCase().includes(kw))
    );

    if (matches.length > 0) {
      const selected = matches.find(r => r.id === savedSelections[key]) ?? matches[0];
      ruleConfigs.push({
        key,
        candidates: matches.map(r => ({ id: r.id, name: r.name })),
        id:         selected.id,
        name:       selected.name,
        enabled:    true,
        weight:     savedWeights[key] ?? DEFAULT_WEIGHTS[key],
      });
    }
    // If no rule matches for this category, it's simply omitted from the list
  }

  // ── Active drivers ─────────────────────────────────────────────────────────
  const userMap = new Map<string, string>();
  rawUsers
    .filter(u =>
      u.id &&
      u.id !== 'UnknownDriverId' &&
      u.isDriver === true &&
      (!u.activeTo || new Date(u.activeTo) > now)
    )
    .forEach(u => {
      const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
        || u.name || u.id;
      userMap.set(u.id, full);
    });

  // ── Active vehicles (must have serial number) ──────────────────────────────
  const devices: SafetyDevice[] = rawDevices
    .filter(d => d.id && d.name && d.serialNumber && (!d.activeTo || new Date(d.activeTo) > now))
    .map(d => ({
      id:       d.id,
      name:     d.name,
      groupIds: (d.groups ?? []).map(g => g.id),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // ── Group hierarchy ─────────────────────────────────────────────────────────
  const { groupMap, groups, groupChildren } = buildGroupHierarchy(rawGroups);

  return { ruleConfigs, userMap, devices, groupMap, groups, groupChildren };
}

// ── localStorage helpers ───────────────────────────────────────────────────

export function saveWeights(configs: RuleConfig[]): void {
  const map: Record<string, number> = {};
  configs.forEach(c => { map[c.key] = c.weight; });
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch { /* quota */ }
}

function loadSavedWeights(): Partial<Record<RuleKey, number>> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
